import { useMemo } from 'react';
import { FilterForm, SelectInput } from 'react-admin';
import { Card, CardContent } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { useWatch } from 'react-hook-form';

// SelectInput, a diferencia de TextInput, siempre dibuja su label ya
// "achicado" sobre el borde (ver MuiFormLabel mas abajo) aunque el campo
// este vacio -- no hay nada elegido todavia, pero el titulo igual sube, lo
// que se ve raro al lado de los campos de texto (que si muestran el titulo
// grande y adentro cuando estan vacios). TextInput decide esto solo (MUI
// mira si el input tiene contenido), pero SelectInput no lo expone de forma
// confiable, asi que este wrapper lee el valor real del campo en el
// formulario de filtros (useWatch, del mismo react-hook-form que usa
// react-admin por dentro) y solo sube el titulo si de verdad hay algo
// elegido -- igual que TextInput.
// NOTA: se intento el mismo truco para DateInput (mandandole el shrink via
// slotProps.inputLabel, que es lo que esa version de MUI respeta), y
// funcionaba, pero con el campo vacio el titulo grande queda superpuesto
// con el "mm/dd/aaaa" que el propio navegador dibuja adentro de un
// <input type="date"> vacio -- se ve peor que antes. Por eso las fechas se
// dejaron con el DateInput normal de react-admin (titulo siempre arriba).
export const ShrinkAwareSelectInput = ({ source, ...rest }) => {
    const value = useWatch({ name: source });
    return <SelectInput {...rest} source={source} InputLabelProps={{ shrink: !!value }} />;
};

const FilterSidebar = ({ filters }) => {
    // Antes este theme se creaba con createTheme({...}) "a secas", lo cual
    // REEMPLAZA por completo el theme de la app (el que viene de <Admin theme={theme}>
    // en App.jsx) dentro de este subarbol, en lugar de extenderlo. Eso hacia que se
    // perdiera el defaultProps `variant: 'filled'` que trae defaultLightTheme de
    // react-admin, y los inputs de PostFilter (TextInput/SelectInput sin variant
    // explicito) caian al variant por defecto de MUI ('outlined'), que trae su propio
    // borde (fieldset) ademas del borde que este archivo agrega via MuiInputBase -> de
    // ahi la doble borde, las esquinas sin redondear (el borderRadius solo estaba
    // definido para MuiFilledInput) y los tamanos inconsistentes entre campos.
    // Ahora se construye el theme extendiendo el theme ambiente (createTheme(outerTheme, {...}))
    // y ademas se fuerza explicitamente variant: 'filled' para que quede garantizado
    // sin depender solo de la herencia.
    const outerTheme = useTheme();

    const theme = useMemo(() => createTheme(outerTheme, {
        components: {
            // El <div> que envuelve cada campo (RaFilterFormInput, mas abajo)
            // es un flex container con DOS hijos: el campo en si y un
            // separador fijo de 16px que react-admin agrega siempre. Un
            // TextInput y un SelectInput usan el mismo MuiFormControl por
            // dentro, pero "width: 100%" en un hijo flex no es un ancho fijo,
            // es la base para repartir espacio (flex-basis), y esa cuenta le
            // daba resultados distintos a cada campo (por eso se veian de
            // ancho distinto aunque los dos tenian fullWidth). Con
            // flex: '1 1 0%' el campo simplemente crece para llenar TODO el
            // espacio que sobra despues del separador de 16px, sin depender
            // de esa cuenta - igual para texto y para combos.
            MuiFormControl: {
                defaultProps: {
                    variant: 'filled',
                },
                styleOverrides: {
                    root: {
                        flex: '1 1 0%',
                        minWidth: 0,
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'filled',
                },
            },
            // DateInput (ra-ui-materialui/src/input/DateInput.tsx) NO pasa por el mismo
            // canal que TextInput/SelectInput para su ancho: no destructura ni manda
            // "fullWidth" explicito -- depende PURO de defaultProps de tema, pero via su
            // PROPIO nombre de componente registrado ("RaDateInput", el "name" que le pasa
            // a useThemeProps), no via MuiFormControl/MuiTextField de arriba. Por eso el
            // "flex: '1 1 0%'" de MuiFormControl no alcanzaba a estirarlo: el ancho del
            // <input type="date"> nativo quedaba resuelto por su contenido (mm/dd/aaaa +
            // icono de calendario) en vez de llenar el campo, mas angosto que Banco/Cuenta/
            // Tipo. Este es el punto de personalizacion que la propia libreria expone para
            // este componente (ver el "declare module" al final de DateInput.tsx).
            RaDateInput: {
                defaultProps: {
                    fullWidth: true,
                    variant: 'filled',
                },
                styleOverrides: {
                    root: {
                        width: '100%',
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontFamily: 'Gothic',
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'white',
                        border: '2px solid #ccc',
                        borderRadius: '7px',
                        fontFamily: 'Gothic',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        height: '2.6rem'
                    },
                    // Respaldo del mismo ajuste que MuiFilledInput.sizeSmall (ver ese
                    // comentario): esta clase (.MuiInputBase-sizeSmall) es la que
                    // DateInput termina usando ademas de la especifica del variant, asi
                    // que se iguala aca tambien por las dudas.
                    sizeSmall: {
                        height: '2.6rem',
                    },
                },
            },
            // El ancho ya quedo parejo (RaFilterFormInput mas abajo), pero el
            // alto seguia distinto: el <input> de texto usa el padding normal
            // de "filled" (25px arriba / 8px abajo, pensado para que el label
            // se achique hacia arriba), mientras que el valor de un <Select>
            // (.MuiSelect-select) trae su PROPIO padding por defecto de MUI que
            // no es igual, y ademas la clase de tamano "small"
            // (.MuiInputBase-sizeSmall) que se aplica via "variants" pisa el
            // height que se puso en MuiInputBase de arriba (los variants se
            // inyectan despues que los styleOverrides, asi que ganan ellos).
            // Por eso se fuerza el alto y el padding vertical en el propio
            // input/select, con el mismo valor para los dos, en vez de confiar
            // en el height del root.
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'white',
                        borderRadius: '7px',
                        borderTopRightRadius: '7px',
                        borderTopLeftRadius: '7px',
                        height: '2.6rem',
                        boxSizing: 'border-box',
                        '&:before': {
                            borderBottom: 'none',
                            borderRadius: '7px',
                        },
                        '&:after': {
                            borderBottom: 'none',
                            borderRadius: '7px',
                        },
                    },
                    input: {
                        height: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        // El label achicado ya no vive ADENTRO del campo (ver
                        // MuiFormLabel mas abajo: ahora queda montado sobre el borde
                        // superior, como el label "con muesca" de un input outlined),
                        // asi que el valor puede volver a centrarse verticalmente sin
                        // pelear por espacio con el.
                        alignItems: 'center',
                        paddingTop: 0,
                        paddingBottom: 0,
                    },
                    // DateInput (ra-ui-materialui/src/input/DateInput.tsx) le pone
                    // size="small" a su TextField interno de forma fija -- no es algo
                    // que este pantalla o su tema puedan pisar via defaultProps, es un
                    // atributo literal en el JSX de esa libreria. Eso hacia que "Fecha
                    // Desde"/"Fecha Hasta" quedaran mas chicos que Banco/Cuenta/Tipo (que
                    // no fuerzan ningun size): el tamano "small" de MUI inyecta su PROPIA
                    // clase (.MuiFilledInput-sizeSmall) con un alto distinto al de
                    // "root" de arriba, y esa clase gana porque as su selector especifico
                    // para esa variante, no una simple cascada de "root". Se iguala aca
                    // el mismo alto/padding que "root"/"input" de arriba para que un
                    // campo de fecha (que siempre es "small") se vea IGUAL a uno que no
                    // lo es.
                    sizeSmall: {
                        height: '2.6rem',
                    },
                    inputSizeSmall: {
                        height: '100%',
                        boxSizing: 'border-box',
                        paddingTop: 0,
                        paddingBottom: 0,
                    },
                },
            },
            // .MuiSelect-select trae su propio padding/alto por defecto de MUI
            // (distinto al de un input de texto) con especificidad alta, asi
            // que hace falta !important para poder igualarlo al de arriba.
            MuiSelect: {
                styleOverrides: {
                    select: {
                        height: '100% !important',
                        minHeight: 'unset !important',
                        boxSizing: 'border-box',
                        display: 'flex !important',
                        // Mismo motivo que MuiFilledInput.input de arriba.
                        alignItems: 'center',
                        paddingTop: '0 !important',
                        paddingBottom: '0 !important',
                    },
                },
            },
            RaFilterForm: {
                styleOverrides: {
                    root: {
                        width: '100%',
                        marginTop: '0.1rem'
                    },
                },
            },
            // El formulario de filtros (RaFilterForm) es un flex container
            // con flexWrap. Sin esto cada FilterFormInput ocuparia solo el
            // ancho de su contenido y varios campos podrian quedar en la
            // misma fila; forzandolo a 100% cada campo cae en su propia fila
            // (como en el diseno original) y ademas le da al MuiFormControl
            // de adentro un ancho de referencia consistente para repartir
            // (ver flex: '1 1 0%' en MuiFormControl, arriba).
            RaFilterFormInput: {
                styleOverrides: {
                    root: {
                        width: '100%',
                        // Espacio vertical entre un campo y el siguiente. Antes no hacia
                        // falta casi nada porque el label vivia adentro del campo, pero
                        // ahora que el label achicado se dibuja montado sobre el borde
                        // superior (ver MuiFormLabel mas abajo) necesita aire arriba para
                        // no quedar pegado al campo de arriba.
                        marginTop: '14px',
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        fontFamily: 'Gothic',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        color: '#191268',
                        width: '100%',
                        // El label "achicado" (shrink, cuando el campo tiene foco o
                        // valor) trae por defecto de MUI un transform pensado para un
                        // filled input de ~56px de alto; el nuestro mide 2.6rem
                        // (~41.6px, ver MuiInputBase/MuiFilledInput arriba), asi que
                        // metido ADENTRO del campo no hay espacio limpio para el sin
                        // que se superponga con el valor (primer intento: quedaba muy
                        // arriba y muy chico, dificil de leer). En vez de pelear por ese
                        // espacio interno, el label achicado se saca del campo: queda
                        // montado sobre el propio borde superior (como el label "con
                        // muesca" de un input outlined), con fondo blanco propio para
                        // tapar el pedacito de borde que le queda atras.
                        '&.MuiInputLabel-shrink': {
                            fontSize: '0.7em',
                            lineHeight: 1.3,
                            backgroundColor: '#fff',
                            padding: '0 4px',
                            // translate Y negativo saca el label del campo hacia arriba,
                            // centrandolo sobre la linea del borde. Si no queda bien
                            // centrado sobre la linea, avisame por cuantos px y lo afino.
                            transform: 'translate(6px, -9px) scale(1)',
                            // El width:100% de arriba (pensado para el label GRANDE, que
                            // necesita todo el ancho del campo para no cortar textos
                            // largos) tambien se heredaba aca, y hacia que el fondo
                            // blanco de este label achicado tapara casi todo el borde
                            // superior del lado derecho en vez de solo el pedacito
                            // detras del texto. En este estado el label ya no necesita
                            // ese ancho (esta montado sobre el borde, no adentro del
                            // campo), asi que vuelve a su ancho natural (el del texto).
                            width: 'auto',
                            maxWidth: 'calc(100% - 12px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            zIndex: 1,
                        },
                    },
                },
            },
        },
    }), [outerTheme]);

    return (
        <ThemeProvider theme={theme}>
            {/* mt alinea este Card con el Card de la lista: cuando el <List> tiene
                `actions`, react-admin agrega arriba de la lista una barra
                (RaTopToolbar, variant dense) que este aside NO recibe porque se
                renderiza como hermano del bloque principal, no adentro de el.
                Con box-sizing:border-box (que MUI aplica globalmente) esa barra
                mide ~48px (minHeight del Toolbar dense; el padding queda adentro
                de esos 48px, no se suma aparte). El valor anterior (60px) no
                tomaba eso en cuenta y por eso quedaba un poco mas abajo de la
                cuenta. Si todavia no calza exacto, avisame por cuantos px y lo
                afino. */}
            <Card sx={{ order: -1, mr: 2, mt: '48px', width: 250 }}>
                <CardContent>
                    <FilterForm filters={filters} />
                </CardContent>
            </Card>
        </ThemeProvider>)
};

export default FilterSidebar;
